import React, { useState, useEffect } from "react";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as suitableService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";

const Suitable = () => {
  const queryClient = useQueryClient();

  // State management
  const [selectedSuitableItem, setSelectedSuitableItem] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [suitableName, setSuitableName] = useState("");
  const [suitableStatus, setSuitableStatus] = useState(1);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [errors, setErrors] = useState({
    name: "",
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: suitableData } = useQuery({
    queryKey: ["suitable", currentPage, activeSearch],
    queryFn: () =>
      suitableService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: suitableService.create,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Suitable item added successfully!");
      setAddDialogOpen(false);
      setSuitableName("");
      setErrors({ name: "" });
      queryClient.invalidateQueries(["suitable"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.response?.data?.message || "Failed to add suitable item");
    },
  });

  const updateMutation = useMutation({
    mutationFn: suitableService.update,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Suitable item updated successfully!");
      setEditDialogOpen(false);
      setSuitableName("");
      setErrors({ name: "" });
      queryClient.invalidateQueries(["suitable"]);
    },
    onError: (error) => {
      setIsLoading(false);
        showErrorMessage(
        error.response?.data?.message || "Failed to update suitable item"
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: suitableService.deleteSuitable,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.response?.data?.message || "Suitable item deleted successfully!");
      setDeleteDialogOpen(false);
      queryClient.invalidateQueries(["suitable"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || "Failed to delete suitable item"
      );
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: suitableService.getById,
    onSuccess: (data) => {
      setIsLoading(false);
      setSelectedSuitableItem(data.data);
      setSuitableName(data.data.name);
      setSuitableStatus(data.data.status);
      setEditDialogOpen(true);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || "Failed to fetch suitable item details"
      );
    },
  });

  // Validation
  const validateForm = () => {
    let tempErrors = {
      name: "",
    };
    let isValid = true;

    if (!suitableName.trim()) {
      tempErrors.name = "Suitable item name is required";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  // Helper functions
  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setErrorMessage(errorMsg);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  // Action handlers
  const handleAddClick = () => {
    setSuitableName("");
    setSuitableStatus(1);
    setAddDialogOpen(true);
  };

  const handleDeleteClick = (suitableItem) => {
    setSelectedSuitableItem(suitableItem);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (suitableId) => {
    getByIdMutation.mutate(suitableId);
  };

  const handleSaveSuitableItem = () => {
    setIsLoading(true);
    if (validateForm()) {
      createMutation.mutate({
        name: suitableName,
        status: Number(suitableStatus),
      });
    }else{
      setIsLoading(false);
    }
  };

  const handleUpdateSuitableItem = () => {
    setIsLoading(true);
    if (validateForm()) {
      updateMutation.mutate({
        id: selectedSuitableItem.id,
        suitable: { name: suitableName, status: Number(suitableStatus) },
      });
    }else{
      setIsLoading(false);
    }
  };

  const handleDeleteSuitableItem = () => {
    setIsLoading(true);
    if (selectedSuitableItem) {
      deleteMutation.mutate(selectedSuitableItem.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const suitableItems = suitableData?.data?.data || [];
  const pagination = {
    currentPage: suitableData?.current_page || 1,
    lastPage: suitableData?.last_page || 1,
    perPage: suitableData?.per_page || 10,
    total: suitableData?.total || 0,
    from: suitableData?.from || 0,
    to: suitableData?.to || 0,
  };

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.suitable.title}
        subtitle={ADMIN_PAGE_META.suitable.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              placeholder="Search suitable items..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border border-primary shadow-md hover:bg-primary/90 transition-all"
            >
              Add Suitable For
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            <AdminTableFooter>
              <Pagination
                pagination={pagination}
                onPageChange={handlePageChange}
                showInfo={true}
              />
            </AdminTableFooter>
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Suitable Items</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {suitableItems.length > 0 ? (
            suitableItems.map((suitableItem, index) => (
              <AdminTableRow key={suitableItem.id}>
                <AdminTableCell>
                  {(pagination.currentPage - 1) * pagination.perPage + index + 1}
                </AdminTableCell>
                <AdminTableCell>{suitableItem.name}</AdminTableCell>
                <AdminTableCell>
                  <StatusBadge variant={getStatusVariant(suitableItem.status)}>
                    {suitableItem.status === 1 ? "Active" : "In-Active"}
                  </StatusBadge>
                </AdminTableCell>
                <TableActionsCell>
                  <EditActionButton
                    showLabel={false}
                    onClick={() => handleEditClick(suitableItem.id)}
                  />
                  <DeleteActionButton
                    showLabel={false}
                    onClick={() => handleDeleteClick(suitableItem)}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow
              colSpan={4}
              emptyMessage="No suitable items found"
              searchQuery={activeSearch}
              onClearSearch={clearSearch}
            />
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setErrors({ name: "" });
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Suitable Item
          </div>
        }
        primaryButtonText="Add Suitable Item"
        primaryButtonAction={handleSaveSuitableItem}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setErrors({ name: "" });
        }}
        width="w-[500px]"
        isLoading={isLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              className={`text-sm w-full px-3 py-1 border ${
                errors.name ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
              placeholder="Enter suitable item name"
              value={suitableName}
              onChange={(e) => {
                setSuitableName(e.target.value);
                setErrors({ ...errors, name: "" });
              }}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={suitableStatus}
              onChange={(e) => setSuitableStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>In-Active</option>
            </select>
          </div>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setErrors({ name: "" });
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Suitable Item
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateSuitableItem}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setEditDialogOpen(false);
          setErrors({ name: "" });
        }}
        width="w-[500px]"
        isLoading={isLoading}
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suitable Item Name
            </label>
            <input
              type="text"
              className={`text-sm w-full px-3 py-1 border ${
                errors.name ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary`}
              placeholder="Enter suitable item name"
              value={suitableName}
              onChange={(e) => {
                setSuitableName(e.target.value);
                setErrors({ ...errors, name: "" });
              }}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name}</p>
            )}
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              value={suitableStatus}
              onChange={(e) => setSuitableStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>In-Active</option>
            </select>
          </div>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Suitable Item
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteSuitableItem}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this suitable item? This action
            cannot be undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default Suitable;
